import React, { useCallback } from 'react';
import { useGetFileFromStorage } from 'hooks/useGetFileFromStorage';
import {
  FileOutput,
  GetFhirAppointmentDiagnosticReportOutput,
} from 'graphql/generated/hasura';
import { ReactComponent as TestKitIcon } from 'assets/icons/testKits/testKit.svg';
import { useGetComponent } from '../../../../hooks/useGetComponent';
import { componentIds } from '../../../../utilities/constants';
import ButtonComponent from '../../../../components/button/buttonComponent';
import { formatDateForAppointment } from '../../../../utilities/functions';
import moment from 'moment';

interface TestKitsBlockProps {
  patientId: string;
  tests: GetFhirAppointmentDiagnosticReportOutput[];
  sendPdfToChat: (file: FileOutput) => void;
}

const TestKitsBlock: React.FC<TestKitsBlockProps> = ({
  patientId,
  tests = [],
  sendPdfToChat,
}) => {
  const { data: locale } = useGetComponent({
    componentId: componentIds.TEST_KIT_LABELS,
    locale: 'en',
  });

  const { fetchFileFromStorage } = useGetFileFromStorage();

  const getDownloadFunction = useCallback(
    async (diagnosticReportId: string) => {
      if (!diagnosticReportId) return;
      const pdfLink = await fetchFileFromStorage(diagnosticReportId, patientId);
      window.open(pdfLink, '_blank');
    },
    [fetchFileFromStorage, patientId],
  );

  if (!tests.length) {
    return null;
  }

  return (
    <div className="-mt-3 -ml-3.5 h-full">
      {tests.map((test) => {
        const file = test.providerPdf;

        if (!file) {
          return null;
        }
        const { fileId, creation } = file;
        return (
          <div className="group flex gap-2.5 py-5 px-4 text-left text-neutral-800 font-semibold">
            <TestKitIcon className="w-6 h-6 max-w-[24px]" />
            <div className="flex flex-col gap-2.5">
              <div className=" text-btn-black font-semibold">
                {locale?.[test.diagnosticReportTestType] ??
                  test.diagnosticReportTestType}

                <div className="text-med-gray-3 text-sm">
                  {formatDateForAppointment(
                    moment(creation || undefined).toDate(),
                    {
                      simplified: true,
                      withTimeZone: false,
                    },
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                <ButtonComponent
                  type="underline"
                  testID="open-test-kit-result"
                  onClick={() => getDownloadFunction(fileId ?? '')}
                >
                  Open in new tab
                </ButtonComponent>

                <ButtonComponent
                  type="underline"
                  testID="open-test-kit-result"
                  onClick={() => sendPdfToChat(file)}
                >
                  Share with patient
                </ButtonComponent>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TestKitsBlock;
